import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Form, Formik, useFormikContext } from "formik";
import { assetTypeEnum } from "kubb";
import { isEqual } from "lodash-es";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ButtonGroupField } from "../../../../components/Forms/ButtonGroup";
import { SelectComboField } from "../../../../components/Forms/SelectCombo/SelectCombo";
import { Button } from "../../../../components/Theme/button";
import { Col, Container } from "../../../../components/Theme/grid";
import {
  convertStringUndefined,
  createSearchString,
} from "../../../../components/navigation/SearchString";
import Spinner from "../../../../core/action/Spinner";
import {
  filterOptionsToSelectOptions,
  useAssetFilterOptions,
} from "../../../../hooks/useAssetFilterOptions";

export interface GatewayFilterParams {
  manufacturer?: string[];
  model?: string[];
  firmware_version?: string[];
  configuration_state?: string[];
  connectivity?: string[];
  auto_sync?: string;
  sync_devices?: string;
}

interface Props {
  company_id: string;
  scheme_id?: string;
  params: GatewayFilterParams;
  assetPositionId?: "NONE" | "NOT_NONE";
  onSubmit?: (values: GatewayFilterParams) => void;
}

const orUndefined = (arr: string[] | undefined): string[] | undefined =>
  arr?.length ? arr : undefined;

export default function GatewayFilterForm(props: Props): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id, scheme_id, params, assetPositionId = "NOT_NONE" } = props;

  const setFiltersToRoute = useCallback(
    (values: GatewayFilterParams): void => {
      const cleaned = convertStringUndefined({ ...values });
      if (cleaned.auto_sync === "") delete cleaned.auto_sync;
      if (cleaned.sync_devices === "") delete cleaned.sync_devices;
      const newSearchString = createSearchString(cleaned);

      if (
        location.search === ""
          ? newSearchString !== ""
          : `?${newSearchString}` !== location.search
      ) {
        navigate({
          ...location,
          search: newSearchString,
        });
      }
    },
    [navigate, location],
  );

  const currentValues: GatewayFilterParams = {
    manufacturer: params.manufacturer || [],
    model: params.model || [],
    firmware_version: params.firmware_version || [],
    configuration_state: params.configuration_state || [],
    connectivity: params.connectivity || [],
    auto_sync: params.auto_sync ?? "",
    sync_devices: params.sync_devices ?? "",
  };

  return (
    <Formik
      initialValues={currentValues}
      enableReinitialize
      onSubmit={(values: GatewayFilterParams): void => {
        if (props.onSubmit) {
          props.onSubmit(values);
        } else {
          setFiltersToRoute(values);
        }
      }}
    >
      <GatewayFilterFormFields
        company_id={company_id}
        scheme_id={scheme_id}
        assetPositionId={assetPositionId}
        currentValues={currentValues}
      />
    </Formik>
  );
}

function hasAdvancedFilters(params: GatewayFilterParams): boolean {
  return !!(
    params.firmware_version?.length ||
    params.connectivity?.length ||
    (params.auto_sync && params.auto_sync !== "") ||
    (params.sync_devices && params.sync_devices !== "")
  );
}

function GatewayFilterFormFields({
  company_id,
  scheme_id,
  assetPositionId,
  currentValues,
}: {
  company_id: string;
  scheme_id?: string;
  assetPositionId: "NONE" | "NOT_NONE";
  currentValues: GatewayFilterParams;
}): React.ReactElement {
  const { values } = useFormikContext<GatewayFilterParams>();
  const [advancedOpen, setAdvancedOpen] = useState(() =>
    hasAdvancedFilters(currentValues),
  );

  // Fetch filter options using current form values (updates as user changes selections)
  const {
    data: filterOptions,
    isLoading,
    isFetching,
    isError,
  } = useAssetFilterOptions({
    company_id,
    scheme_id: scheme_id || undefined,
    asset_type: [assetTypeEnum.gateway],
    asset_position_id: assetPositionId,
    manufacturer: orUndefined(values.manufacturer),
    model: orUndefined(values.model),
    firmware_version: orUndefined(values.firmware_version),
    configuration_state: orUndefined(values.configuration_state),
    connectivity: orUndefined(values.connectivity),
    auto_sync: values.auto_sync ? values.auto_sync === "true" : undefined,
    sync_devices: values.sync_devices
      ? values.sync_devices === "true"
      : undefined,
  });

  // Convert filter options to SelectCombo options
  const manufacturerOptions = useMemo(
    () => filterOptionsToSelectOptions(filterOptions?.manufacturer),
    [filterOptions?.manufacturer],
  );

  const modelOptions = useMemo(
    () => filterOptionsToSelectOptions(filterOptions?.model),
    [filterOptions?.model],
  );

  const firmwareOptions = useMemo(
    () => filterOptionsToSelectOptions(filterOptions?.firmware_version),
    [filterOptions?.firmware_version],
  );

  const configStateOptions = useMemo(
    () => filterOptionsToSelectOptions(filterOptions?.configuration_state),
    [filterOptions?.configuration_state],
  );

  return (
    <Form>
      {isError && (
        <div className="text-red-600 text-sm mb-2">
          Failed to load filter options. Please try again.
        </div>
      )}
      <Container gap={2}>
        <Col sm={12} md={3}>
          <SelectComboField
            fieldName="manufacturer"
            label={{ label: "Manufacturer", size: "sm" }}
            options={manufacturerOptions}
            isMulti={true}
            placeholder={
              isLoading ? "Loading..." : isError ? "Error" : "All manufacturers"
            }
            disabled={isLoading}
          />
        </Col>
        <Col sm={12} md={3}>
          <SelectComboField
            fieldName="model"
            label={{ label: "Model", size: "sm" }}
            options={modelOptions}
            isMulti={true}
            placeholder={
              isLoading ? "Loading..." : isError ? "Error" : "All models"
            }
            disabled={isLoading}
          />
        </Col>
        <Col sm={12} md={3}>
          <SelectComboField
            fieldName="configuration_state"
            label={{ label: "Config State", size: "sm" }}
            options={configStateOptions}
            isMulti={true}
            placeholder={
              isLoading ? "Loading..." : isError ? "Error" : "All states"
            }
            disabled={isLoading || !filterOptions?.configuration_state}
          />
        </Col>
        <Col sm={12} md={3} className="flex items-end gap-2">
          <Button
            type="submit"
            className="w-full h-[38px]"
            disabled={isEqual(values, currentValues)}
          >
            Filter
          </Button>
          <Spinner
            spin={isFetching && !isLoading}
            className="text-zinc-400 mb-2"
          />
        </Col>
      </Container>
      <button
        type="button"
        onClick={() => setAdvancedOpen(!advancedOpen)}
        className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 mt-2 mb-1"
      >
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`}
        />
        Advanced filters
      </button>
      {advancedOpen && (
        <Container gap={2}>
          <Col sm={12} md={3}>
            <SelectComboField
              fieldName="firmware_version"
              label={{ label: "Firmware", size: "sm" }}
              options={firmwareOptions}
              isMulti={true}
              placeholder={
                isLoading ? "Loading..." : isError ? "Error" : "All versions"
              }
              disabled={isLoading}
            />
          </Col>
          <Col sm={12} md={3}>
            <ButtonGroupField
              fieldName="connectivity"
              label={{ label: "Connectivity", size: "sm" }}
              options={[
                { label: "All", value: [] },
                { label: "Online", value: ["ONLINE"] },
                { label: "Offline", value: ["OFFLINE"] },
              ]}
              fullWidth={true}
            />
          </Col>
          <Col sm={12} md={3}>
            <ButtonGroupField
              fieldName="auto_sync"
              label={{ label: "Auto Sync", size: "sm" }}
              options={[
                { label: "All", value: "" },
                { label: "On", value: "true" },
                { label: "Off", value: "false" },
              ]}
              fullWidth={true}
            />
          </Col>
          <Col sm={12} md={3}>
            <ButtonGroupField
              fieldName="sync_devices"
              label={{ label: "Sync Devices", size: "sm" }}
              options={[
                { label: "All", value: "" },
                { label: "On", value: "true" },
                { label: "Off", value: "false" },
              ]}
              fullWidth={true}
            />
          </Col>
        </Container>
      )}
    </Form>
  );
}
